import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Product from "../product/Product";
import SideFilters from "./SideFilters";
import MobileFiltersDrawer from "./MobileFiltersDrawer";
import {
  SimpleGrid,
  Box,
  Spinner,
  Flex,
  Grid,
  Text,
  Button,
} from "@chakra-ui/react";
import SelectC from "../../theme/SelectC";
import { useDispatch, useSelector } from "react-redux";
import { getProducts, getFilters, clearProducts } from "../../slices/shop";
import { useSearchParams } from "react-router-dom";

export default function Filters() {
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const max_price = searchParams.get("max_price");

  const dispatch = useDispatch();
  const {
    productList,
    filters: backendFilters,
    filtersStatus,
    status,
    loadingMore,
  } = useSelector((state) => state.shop);

  const products = productList?.items || [];
  const totalItems = productList?.total || 0;
  const currentProductCount = products.length;

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const isInitialLoading = status === "loading";
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    categories: category || "",
    colors: "",
    sizes: "",
    sort_by: "created_at",
    min_price: 0,
    max_price: max_price || 5000,
    sort_order: "desc",
  });

  const sortOptions = [
    { label: "Featured", value: "featured" },
    { label: "Price: Low to High", value: "price_low" },
    { label: "Price: High to Low", value: "price_high" },
    { label: "Newest", value: "newest" },
  ];

    const [sortBy, setSortBy] = useState();
  console.log(sortBy);

  const removeBlankKeys = (obj) =>
    Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        acc[key] = value;
      }
      return acc;
    }, {});

  // --- ACTIVE FILTER FLAGS / COUNT (for badges) ---
  const activeFlags = useMemo(
    () => ({
      categories: !!filters.categories,
      colors: !!filters.colors,
      sizes: !!filters.sizes,
      price:
        Number(filters.min_price) !== 0 ||
        Number(filters.max_price) !== Number(max_price || 5000),
      sort: !!filters.sort_by,
    }),
    [filters, max_price]
  );

  const activeCount = useMemo(
    () => Object.values(activeFlags).filter(Boolean).length,
    [activeFlags]
  );

  useEffect(() => {
    dispatch(getFilters());
  }, [dispatch]);

  useEffect(() => {
    if (category) {
      setFilters((prev) => ({ ...prev, categories: category }));
    }
  }, [category]);

  useEffect(() => {
    dispatch(clearProducts());
    setPage(1);
    setHasMore(true);
    dispatch(getProducts({ filters: removeBlankKeys(filters), page: 1 }));
  }, [filters, dispatch]);

  useEffect(() => {
    if (page > 1) {
      dispatch(getProducts({ filters: removeBlankKeys(filters), page }));
    }
  }, [page, filters, dispatch]);

  useEffect(() => {
    if (status === "succeeded" || loadingMore === false) {
      if (currentProductCount >= totalItems && totalItems > 0) {
        setHasMore(false);
      } else if (currentProductCount < totalItems) {
        setHasMore(true);
      } else if (totalItems === 0 && products.length === 0) {
        setHasMore(false);
      }
    }
  }, [currentProductCount, totalItems, status, loadingMore, products.length]);

  const observer = useRef(null);

  const lastProductRef = useCallback(
    (node) => {
      if (loadingMore || isInitialLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prev) => prev + 1);
          }
        },
        { threshold: 0.5, rootMargin: "200px" }
      );

      if (node) observer.current.observe(node);
    },
    [loadingMore, isInitialLoading, hasMore]
  );

  const handleFilterChange = (filter, value) => {
    if (filter === "price") {
      setFilters((prev) => ({
        ...prev,
        min_price: value[0],
        max_price: value[1],
      }));
    } else if (filter === "sort_by") {
      if (value === "newest" || value === "featured") {
        setFilters((prev) => ({
          ...prev,
          sort_by: "created_at",
          sort_order: "desc",
        }));
      } else if (value === "price_low") {
        setFilters((prev) => ({
          ...prev,
          sort_by: "list_price",
          sort_order: "asc",
        }));
      } else if (value === "price_high") {
        setFilters((prev) => ({
          ...prev,
          sort_by: "list_price",
          sort_order: "desc",
        }));
      }
    } else {
      setFilters((prev) => ({ ...prev, [filter]: value }));
    }
  };

  const clearAllFilter = () => {
    setFilters({
      categories: "",
      colors: "",
      sizes: "",
      sort_by: "",
      min_price: 0,
      max_price: 5000,
      sort_order: "",
    });
  };

  const refreshFilters = () => {
    dispatch(clearProducts());
    setPage(1);
    setHasMore(true);
    dispatch(getProducts({ filters: removeBlankKeys(filters), page: 1 }));
  };

  if (isInitialLoading && products.length === 0) {
    return (
      <Flex
        h="100vh"
    direction={'column'}
    justifyContent={'center'}
    alignItems={'center'}
        bg="white"
        m={4}
        rounded="lg"
        boxShadow="md"
      >
          
      </Flex>
    );
  }

  return (
    <Box w="full" bg="var(--crt-9)" py={{ base: 40, md: 40 }} px={{ base: 3, md: 6 }}>
      <Flex
        position="sticky"
        top={0}
        zIndex={10}
        bg="var(--crt-9)"
        py={3}
        mb={3}
        align="center"
        justify="space-between"
      >
        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="700">
          Products
          {totalItems ? (
            <Text
              as="span"
              fontSize="sm"
              fontWeight="500"
              color="gray.500"
              ml={2}
            >
              ({totalItems} items)
            </Text>
          ) : null}
        </Text>

        {/* Mobile: Filter button + sort icon */}
        <Flex align="center" gap={2} display={{ base: "flex", md: "none" }}>
          {activeCount > 0 && (
            <Box
              fontSize="xs"
              px={2}
              py={1}
              rounded="full"
              bg="black"
              color="white"
            >
              {activeCount} filters
            </Box>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsMobileFilterOpen(true)}
          >
            Filters
          </Button>
        </Flex>

        {/* Desktop: Sort + Clear */}
        <Flex
          align="center"
          gap={3}
          display={{ base: "none", md: "flex" }}
        >
          <SelectC
            size="sm"
            width="200px"
            selectedValue={sortBy ? sortBy.label : null}
            onValueChange={(val) => {
              console.log(val);
              handleFilterChange("sort_by", val.value[0]);
              setSortBy(val.items[0]);
            }}
            items={sortOptions}
          />
          <Button size="sm" variant="outline" onClick={clearAllFilter}>
            Clear Filters
          </Button>
        </Flex>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", md: "270px 1fr" }}
        gap={{ base: 3, md: 6 }}
        alignItems="flex-start"
      >
        {/* Desktop Sidebar */}
        <Box
          display={{ base: "none", md: "block" }}
          position="sticky"
          top="70px"
        >
          <SideFilters
            filters={filters}
            backendFilters={backendFilters}
            handleFilterChange={handleFilterChange}
            clear={clearAllFilter}
            refresh={refreshFilters}
            filtersStatus={filtersStatus}
            activeFlags={activeFlags}
          />
        </Box>

     
        <Box>
          {products.length > 0 ? (
            <>
              <SimpleGrid
                columns={{ base: 1, sm: 2, md: 3, lg: 5 }}
                spacing={{ base: 3, md: 5 }}
                gap={3}
              >
                {products.map((product, index) => {
                  const isLast = index === products.length - 1;
                  if (isLast) {
                    return (
                      <Box ref={lastProductRef} key={product.id}>
                        <Product product={product} />
                      </Box>
                    );
                  }
                  return <Product key={product.id} product={product} />;
                })}
              </SimpleGrid>

              {loadingMore && (
                <Box textAlign="center" py={4}>
                  <Spinner size="lg" />
                </Box>
              )}
            </>
          ) : (
            <Flex
              h="250px"
              align="center"
              justify="center"
              bg="white"
              rounded="lg"
              boxShadow="md"
            >
              No products found
            </Flex>
          )}
        </Box>
      </Grid>

      {/* Mobile Bottom Sheet Filters */}
      <MobileFiltersDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        filters={filters}
        backendFilters={backendFilters}
        handleFilterChange={handleFilterChange}
        clear={clearAllFilter}
        refresh={refreshFilters}
        filtersStatus={filtersStatus}
        activeCount={activeCount}
      />
    </Box>
  );
}
